import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconThumbsUp } from "@/assets/icons/geist/IconThumbsUp";
import { IconThumbsDown } from "@/assets/icons/geist/IconThumbsDown";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { withGitpodSize } from "@/assets/icons/utils";
import { Markdown } from "@/components/ai/chat/Markdown";
import type { ReviewCommentAuthor } from "@/components/ai/review/types";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { cn } from "@/components/podkit/lib/cn";
import { Pill } from "@/components/podkit/status/Pill";
import { Text } from "@/components/podkit/typography/Text";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { useAuthenticatedUser } from "@/queries/user-queries";
import IconChevronRight from "central-icons/IconChevronRight";
import IconPencil from "central-icons/IconPencil";
import IconTrashCan from "central-icons/IconTrashCan";
import { memo, useRef, useState, type FC } from "react";
import { mergeProps, useButton, useDisclosure, type AriaDisclosureProps } from "react-aria";
import { useDisclosureState } from "react-stately";

const IconTrashCanWithSize = withGitpodSize(IconTrashCan);
const IconPencilWithSize = withGitpodSize(IconPencil);

export type InlineCommentAnnotationProps = {
    defaultEditable?: boolean;
    defaultComment?: string;
    startLine: number;
    endLine: number;
    isOutdated?: boolean;
    author?: ReviewCommentAuthor;
    commentId?: string;
    filePath?: string;
    thumbsUp?: boolean;
    thumbsDown?: boolean;
    onSubmit: (content: string) => void;
    onCancel: () => void;
    onDelete?: () => void;
    onThumbsUp?: (thumbsUp: boolean) => void;
    onThumbsDown?: (thumbsDown: boolean) => void;
};

export const InlineCommentAnnotation: FC<InlineCommentAnnotationProps> = memo(
    ({
        defaultEditable = false,
        defaultComment = "",
        startLine,
        endLine,
        isOutdated = false,
        author,
        commentId,
        filePath,
        thumbsUp = false,
        thumbsDown = false,
        onSubmit,
        onCancel,
        onDelete,
        onThumbsUp,
        onThumbsDown,
    }) => {
        const [isExpanded, setIsExpanded] = useState(true);
        const [isEditing, setIsEditing] = useState(defaultEditable);
        const [content, setContent] = useState(defaultComment);
        const [votedUp, setVotedUp] = useTemporaryState(false, 2000);
        const [votedDown, setVotedDown] = useTemporaryState(false, 2000);
        const triggerRef = useRef<HTMLButtonElement>(null);
        const panelRef = useRef<HTMLDivElement>(null);
        const segmentTrack = useSegmentTrack();

        const disclosureProps: AriaDisclosureProps = {
            isExpanded,
            onExpandedChange: setIsExpanded,
            isDisabled: isEditing,
        };

        const state = useDisclosureState(disclosureProps);
        const { buttonProps: triggerProps, panelProps } = useDisclosure(disclosureProps, state, panelRef);
        const { buttonProps } = useButton(triggerProps, triggerRef);

        const handleSubmit = () => {
            if (content.trim()) {
                setIsEditing(false);
                onSubmit(content.trim());
            }
        };

        const handleCancel = () => {
            if (isEditing) {
                setIsEditing(false);
            }
            onCancel();
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSubmit();
            } else if (e.key === "Escape") {
                handleCancel();
            }
        };

        const handleEdit = () => {
            setIsExpanded(true);
            setIsEditing(true);
        };

        const handleThumbsUp = () => {
            const newThumbsUp = !thumbsUp;
            if (!newThumbsUp) {
                onThumbsUp?.(false);
            } else {
                setVotedUp(true);
                // If previously thumbs down, clear it
                if (thumbsDown && onThumbsDown) {
                    setVotedDown(false);
                    onThumbsDown(false);
                }
                onThumbsUp?.(true);
                segmentTrack("agent_comment_voted", {
                    vote: "up",
                    comment_id: commentId,
                    file_path: filePath,
                    start_line: startLine,
                    end_line: endLine,
                });
            }
        };

        const handleThumbsDown = () => {
            const newThumbsDown = !thumbsDown;
            if (!newThumbsDown) {
                // Toggling off - just update state
                onThumbsDown?.(false);
            } else {
                setVotedDown(true);
                setVotedUp(false);
                onThumbsDown?.(true);
                segmentTrack("agent_comment_voted", {
                    vote: "down",
                    comment_id: commentId,
                    file_path: filePath,
                    start_line: startLine,
                    end_line: endLine,
                });
            }
        };

        // Prevent parent diff component from stealing focus when interacting with this annotation
        const handleMouseDown = (e: React.MouseEvent) => {
            e.stopPropagation();
        };

        // Prevent focus events from bubbling to parent
        const handleFocus = (e: React.FocusEvent) => {
            e.stopPropagation();
        };

        const handleBlur = (e: React.FocusEvent) => {
            e.stopPropagation();
        };

        return (
            <div className="p-2 font-sans" onMouseDown={handleMouseDown} onFocus={handleFocus} onBlur={handleBlur}>
                <div className="max-w-xl overflow-clip rounded-md border border-border-base bg-surface-primary shadow-sm">
                    <div
                        className={cn("flex select-none items-center", {
                            "border-b border-border-subtle": state.isExpanded,
                        })}
                    >
                        <Button
                            variant="ghost"
                            size="xs"
                            ref={triggerRef}
                            {...mergeProps(buttonProps)}
                            className="my-0.5 ml-0.5 flex grow items-center gap-0.5 rounded rounded-t-md py-0.5 pl-0.5 enabled:hover:bg-surface-hover disabled:opacity-100"
                        >
                            <span className="flex size-5 items-center justify-center">
                                <IconChevronRight
                                    size="base"
                                    className={cn(
                                        "size-3.5 text-content-primary transition-transform",
                                        state.isExpanded && "rotate-90",
                                    )}
                                />
                            </span>
                            <Text className="flex grow items-center gap-1 text-sm">
                                {/* translation for optical alignment */}
                                <span className="translate-y-[0.5px] text-content-secondary">
                                    {endLine !== startLine ? `Comment on lines` : `Comment on line`}
                                </span>
                                <span className="font-medium">
                                    {endLine !== startLine ? `${startLine}-${endLine}` : startLine}
                                </span>
                                {isOutdated && (
                                    <Pill variant="warning" size="sm" className="ml-1">
                                        Outdated
                                    </Pill>
                                )}
                            </Text>
                        </Button>
                        {!isEditing && (
                            <DropdownActions
                                button={
                                    <Button
                                        variant="ghost"
                                        className="m-1 size-5 rounded"
                                        LeadingIcon={() => (
                                            <IconEllipsisHorizontal
                                                size="base"
                                                className="size-4 text-content-primary"
                                            />
                                        )}
                                    />
                                }
                            >
                                <DropdownMenuItem
                                    LeadingIcon={IconPencilWithSize}
                                    onClick={handleEdit}
                                    data-tracking-id="edit-comment-inline-comment-annotation-dropdown-actions"
                                >
                                    Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    variant="destructive"
                                    LeadingIcon={IconTrashCanWithSize}
                                    onClick={onDelete}
                                    data-tracking-id="delete-comment-inline-comment-annotation-dropdown-actions"
                                >
                                    Delete comment
                                </DropdownMenuItem>
                            </DropdownActions>
                        )}
                    </div>
                    <div
                        ref={panelRef}
                        {...mergeProps(panelProps, {
                            // when disclosure is disabled, the panel is default hidden
                            // which is not desirable because otherwise when editing the textarea will not be visible
                            // https://github.com/adobe/react-spectrum/issues/7444
                            hidden: !isExpanded,
                        })}
                    >
                        {isEditing ? (
                            <div className="p-2">
                                <Textarea
                                    ref={(el) => {
                                        el?.focus()
                                    }}
                                    className="border-border-subtle px-[13px] py-[9px] focus-within:ring-0 focus:px-3 focus:py-2 focus-visible:border-2 focus-visible:border-border-brand focus-visible:ring-0"
                                    minHeight={80}
                                    maxHeight={240}
                                    onChange={(e) => setContent(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Add your comment..."
                                    value={content}
                                />
                            </div>
                        ) : (
                            <div className="p-3">
                                {author && <CommentAuthor author={author} />}
                                <div className="text-wrap break-all text-base text-content-primary">
                                    <Markdown content={content} />
                                </div>
                                {author === "agent" && (
                                    <div className="-ml-1 mt-1 flex items-center gap-1">
                                        <Button
                                            variant="ghost"
                                            size="xs"
                                            className={cn("size-6 rounded p-0", {
                                                "text-green-500": thumbsUp && !votedUp,
                                                "disabled:text-green-500": votedUp,
                                            })}
                                            onClick={handleThumbsUp}
                                            disabled={votedUp}
                                            aria-label="Thumbs up"
                                            data-tracking-id="thumbs-up-agent-comment"
                                        >
                                            {votedUp ? (
                                                <IconCheck size="sm" />
                                            ) : (
                                                <IconThumbsUp
                                                    size="sm"
                                                    className={thumbsUp ? "" : "text-content-secondary"}
                                                />
                                            )}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="xs"
                                            className={cn("size-6 rounded p-0", {
                                                "text-content-destructive": thumbsDown && !votedDown,
                                                "disabled:text-green-500": votedDown,
                                            })}
                                            onClick={handleThumbsDown}
                                            disabled={votedDown}
                                            aria-label="Thumbs down"
                                            data-tracking-id="thumbs-down-agent-comment"
                                        >
                                            {votedDown ? (
                                                <IconCheck size="sm" />
                                            ) : (
                                                <IconThumbsDown
                                                    size="sm"
                                                    className={thumbsDown ? "" : "text-content-secondary"}
                                                />
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                        {isEditing && (
                            <div className="flex justify-end gap-2 border-t border-border-subtle p-2">
                                <Button
                                    variant="outline"
                                    className="rounded"
                                    size="xs"
                                    onClick={handleCancel}
                                    data-tracking-id="cancel-inline-comment"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="xs"
                                    className="rounded"
                                    onClick={handleSubmit}
                                    disabled={!content.trim()}
                                    data-tracking-id="add-inline-comment"
                                >
                                    {defaultComment === "" ? "Add comment" : "Update comment"}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
InlineCommentAnnotation.displayName = "InlineCommentAnnotation";

type CommentAuthorProps = {
    author: ReviewCommentAuthor;
};

const CommentAuthor: FC<CommentAuthorProps> = ({ author }) => {
    const { data: currentUser } = useAuthenticatedUser();

    const isAgent = author === "agent";
    const displayName = isAgent ? "Ona" : "You";
    const avatarName = isAgent ? "Ona" : currentUser?.name || "You";
    const avatarUrl = isAgent ? undefined : currentUser?.avatarUrl;

    return (
        <div className="mb-1.5 flex select-none items-center gap-1.5">
            {isAgent ? (
                <OnaLettermark width={16} height={16} className="shrink-0 text-content-primary" />
            ) : (
                <Avatar size={16}>
                    {avatarUrl && <AvatarImage src={avatarUrl} />}
                    <AvatarFallback>
                        <AvatarInitials name={avatarName} size={16} />
                    </AvatarFallback>
                </Avatar>
            )}
            <Text className="text-base font-medium">{displayName}</Text>
        </div>
    );
};
